import React, { useState, useEffect } from 'react';
import { Utensils, ScanBarcode, Brain, ChevronRight, ChevronLeft, X, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';

const STORAGE_KEY = 'strongfit_onboarding_completed';

const STEP_META = [
  { icon: Sparkles, color: '#FFD700', gradient: 'from-amber-500/20 to-yellow-500/10' },
  { icon: Utensils, color: '#F59E0B', gradient: 'from-orange-500/20 to-amber-500/10' },
  { icon: ScanBarcode, color: '#FFD700', gradient: 'from-yellow-500/20 to-amber-500/10' },
  { icon: Brain, color: '#F59E0B', gradient: 'from-amber-500/20 to-orange-500/10' },
];

const OnboardingWalkthrough = ({ onComplete }) => {
  const { t } = useTranslation();
  const steps = STEP_META.map((m, i) => ({
    ...m,
    title: t(`portal.onboarding.wt_s${i + 1}_title`),
    subtitle: t(`portal.onboarding.wt_s${i + 1}_subtitle`),
    description: t(`portal.onboarding.wt_s${i + 1}_desc`),
  }));
  const [currentStep, setCurrentStep] = useState(0);
  const [visible, setVisible] = useState(false);
  const [exiting, setExiting] = useState(false);

  useEffect(() => {
    const completed = localStorage.getItem(STORAGE_KEY);
    if (!completed) {
      setVisible(true);
    }
  }, []);

  const handleComplete = () => {
    setExiting(true);
    setTimeout(() => {
      localStorage.setItem(STORAGE_KEY, 'true');
      setVisible(false);
      onComplete?.();
    }, 300);
  };

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) setCurrentStep(prev => prev - 1);
  };

  if (!visible) return null;

  const step = steps[currentStep];
  const StepIcon = step.icon;
  const isLast = currentStep === steps.length - 1;

  return (
    <div
      className={`fixed inset-0 z-[100] flex items-center justify-center p-4 transition-opacity duration-300 ${exiting ? 'opacity-0' : 'opacity-100'}`}
      style={{ background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(8px)' }}
      data-testid="onboarding-walkthrough"
    >
      <div
        className="relative w-full max-w-md rounded-2xl overflow-hidden"
        style={{
          background: 'rgba(17,17,17,0.95)',
          border: '1px solid rgba(255,255,255,0.08)',
          boxShadow: '0 24px 64px rgba(0,0,0,0.6)',
        }}
      >
        {/* Close button */}
        <button
          onClick={handleComplete}
          className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full text-white/50 hover:text-white transition-colors z-10"
          style={{ background: 'rgba(255,255,255,0.1)' }}
          data-testid="onboarding-skip-button"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Icon area */}
        <div className={`flex justify-center pt-10 pb-6 bg-gradient-to-b ${step.gradient}`}>
          <div
            className="w-20 h-20 rounded-2xl flex items-center justify-center transition-all duration-500"
            style={{
              background: `linear-gradient(135deg, ${step.color}, ${step.color}88)`,
              boxShadow: `0 8px 32px ${step.color}40`,
            }}
            key={currentStep}
          >
            <StepIcon className="w-10 h-10 text-black" />
          </div>
        </div>

        {/* Content */}
        <div className="px-8 pb-4 text-center" key={`content-${currentStep}`}>
          <h2
            className="text-xl font-bold text-white mb-1 tracking-wide"
            style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
            data-testid="onboarding-step-title"
          >
            {step.title}
          </h2>
          <p className="text-sm text-yellow-400 font-medium mb-4" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
            {step.subtitle}
          </p>
          <p className="text-sm text-white/70 leading-relaxed">
            {step.description}
          </p>
        </div>

        {/* Step dots + navigation */}
        <div className="px-8 pb-8 pt-4">
          {/* Step indicators */}
          <div className="flex justify-center gap-2 mb-6" data-testid="onboarding-step-indicators">
            {steps.map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrentStep(i)}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  i === currentStep ? 'w-8 bg-yellow-400' : 'w-2 bg-white/20 hover:bg-white/30'
                }`}
                data-testid={`onboarding-dot-${i}`}
              />
            ))}
          </div>

          {/* Buttons */}
          <div className="flex items-center gap-3">
            {currentStep > 0 ? (
              <Button
                onClick={handlePrev}
                variant="ghost"
                className="flex-1 h-11 rounded-xl text-white/70 hover:text-white hover:bg-white/10"
                data-testid="onboarding-prev-button"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                {t('portal.onboarding.wt_back')}
              </Button>
            ) : (
              <Button
                onClick={handleComplete}
                variant="ghost"
                className="flex-1 h-11 rounded-xl text-white/50 hover:text-white hover:bg-white/10"
                data-testid="onboarding-skip-text-button"
              >
                {t('portal.onboarding.wt_skip')}
              </Button>
            )}
            <Button
              onClick={handleNext}
              className="flex-1 h-11 rounded-xl font-bold uppercase tracking-wide active:scale-[0.97] transition-all"
              style={{
                background: 'linear-gradient(135deg, #FFD700 0%, #F59E0B 100%)',
                color: '#000',
                boxShadow: '0 2px 16px rgba(234,179,8,0.3)',
              }}
              data-testid="onboarding-next-button"
            >
              {isLast ? t('portal.onboarding.wt_lets_go') : t('portal.onboarding.wt_next')}
              {!isLast && <ChevronRight className="w-4 h-4 ml-1" />}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingWalkthrough;
